import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useAddSecondaryPhone,
  useMakePrimaryPhone,
  useRemoveSecondaryPhone,
} from "@/features/auth/hooks/use-phone-challenge";
import { useUser } from "@/features/auth/hooks/use-user";
import { DotsThreeOutlineIcon, Plus } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "@/features/utils/i18n";
import { parsePhoneNumber } from "react-phone-number-input";
import { AddPhoneDrawer } from "./add-phone-drawer";

export const PhoneTable = () => {
  const { t } = useTranslation();
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
  // The drawer proves the primary instead of adding a number.
  const [verifying, setVerifying] = useState(false);
  const { data: user } = useUser();
  // Typed at sign-up and never reached by a code: not a way to sign in.
  const primaryUnverified = !!user?.phone && user.phone_verified === false;
  const phones = [user?.phone, ...(user?.additional_phones || [])].filter(
    Boolean,
  );

  const makePrimaryMutation = useMakePrimaryPhone();
  const removePhoneMutation = useRemoveSecondaryPhone();
  const addPhoneMutation = useAddSecondaryPhone();

  const handleMakePrimary = async (value: string) => {
    const phone = phones.find((p) => p === value);
    if (!phone) return;

    makePrimaryMutation.mutate({ phone });
  };

  const handleRemove = async (phoneId: string) => {
    const phone = phones.find((p) => p === phoneId);
    if (!phone) return;

    removePhoneMutation.mutate({ phone });
  };

  const handleAddSuccess = async (preSignedToken: string) => {
    addPhoneMutation.mutate(
      { phoneChallengeToken: preSignedToken },
      {
        onSuccess: () => {
          setIsAddDrawerOpen(false);
        },
      },
    );
  };

  const isLoading =
    makePrimaryMutation.isPending ||
    removePhoneMutation.isPending ||
    addPhoneMutation.isPending;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          {t("security.phoneNumbers.description")}
        </p>
        <Button
          onClick={() => {
            setVerifying(false);
            setIsAddDrawerOpen(true);
          }}
          size="sm"
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          {t("security.phoneNumbers.addPhone")}
        </Button>
      </div>

      <div className="rounded-sm border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("security.phoneNumbers.title")}</TableHead>
              <TableHead className="w-[100px] text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {phones.map((phone) => (
              <TableRow key={phone}>
                <TableCell className="font-medium">
                  {parsePhoneNumber(phone!)?.formatInternational() || phone}
                </TableCell>
                <TableCell>
                  {phone === user?.phone && (
                    <div className="flex items-center justify-end gap-2">
                      <Badge variant="default">{t("common.primary")}</Badge>
                      {primaryUnverified && (
                        <>
                          <Badge
                            variant="outline"
                            className="whitespace-nowrap"
                          >
                            {t("security.phoneNumbers.unverified")}
                          </Badge>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={isLoading}
                            onClick={() => {
                              setVerifying(true);
                              setIsAddDrawerOpen(true);
                            }}
                          >
                            {t("security.phoneNumbers.verify")}
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                  {phone !== user?.phone && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          disabled={isLoading}
                        >
                          <span className="sr-only">
                            {t("common.openMenu")}
                          </span>
                          <DotsThreeOutlineIcon
                            weight="fill"
                            className="h-4 w-4"
                          />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => handleMakePrimary(phone!)}
                          disabled={isLoading}
                        >
                          {t("common.makePrimary")}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleRemove(phone!)}
                          className="text-destructive"
                          disabled={isLoading}
                        >
                          {t("common.remove")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {phones.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={3}
                  className="text-center py-8 text-muted-foreground"
                >
                  {t("security.phoneNumbers.noPhones")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <AddPhoneDrawer
        open={isAddDrawerOpen}
        onOpenChange={setIsAddDrawerOpen}
        onSuccess={handleAddSuccess}
        verifyPhone={verifying && user?.phone ? user.phone : undefined}
      />
    </div>
  );
};
